import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import { COLUMN_COUNT, PARTNER_STATUS_CHIP } from '../constants';
import PartnerPreferenceDrugTable from './PartnerPreferenceDrugTable';

const BODY_CELL_SX = { py: 1.25 };

// MUI's Accordion renders a <div>, which is invalid inside <tbody> -- so the
// accordion is built the way the rest of the app builds expandable tables: a
// summary TableRow plus a full-width TableRow holding a Collapse. Reads as an
// accordion, stays valid table markup.
const PartnerPreferenceRow = ({
  partner,
  expanded,
  onToggle,
  preferences,
  loading,
  error,
  onRetry,
  canManage,
  onAddForPartner,
  onChange,
  onRemove,
  onCopyPartnerId,
  partnerStatus,
}) => (
  <>
    <TableRow
      hover
      onClick={onToggle}
      sx={{
        cursor: 'pointer',
        '& > *': { borderBottom: expanded ? 'unset' : undefined },
      }}
    >
      <TableCell sx={BODY_CELL_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant='body2' sx={{ fontWeight: 700 }}>
            {partner.partnerName}
          </Typography>
          {/* Same affordance as the order header: the partner UUID is what you
              need when checking this against an order or a support ticket, but it
              is too long to sit in the row. */}
          <Tooltip title={`Copy partner ID: ${partner.partnerId}`} placement='bottom'>
            <IconButton
              size='small'
              aria-label={`Copy partner ID for ${partner.partnerName}`}
              onClick={(event) => {
                event.stopPropagation();
                navigator.clipboard.writeText(partner.partnerId);
                onCopyPartnerId?.(partner);
              }}
              sx={{ p: 0.25, color: 'text.secondary' }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>

      <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center' }}>
        {/* Partner status, not preference status. Blank while the partner list is
            still loading rather than claiming Active on data we do not have. */}
        {partnerStatus && (
          <Chip
            size='small'
            variant='outlined'
            label={PARTNER_STATUS_CHIP[partnerStatus].label}
            color={PARTNER_STATUS_CHIP[partnerStatus].color}
          />
        )}
      </TableCell>

      <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center', fontWeight: 600 }}>
        {partner.preferenceCount}
      </TableCell>

      <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center', width: 56 }}>
        <IconButton
          size='small'
          aria-label={expanded ? 'Collapse preferences' : 'Expand preferences'}
          aria-expanded={expanded}
          // The row already toggles; this keeps the button from firing it twice.
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        >
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </TableCell>
    </TableRow>

    <TableRow>
      <TableCell
        colSpan={COLUMN_COUNT}
        sx={{ py: 0, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Collapse in={expanded} timeout='auto' unmountOnExit>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
            <PartnerPreferenceDrugTable
              partner={partner}
              preferences={preferences}
              loading={loading}
              error={error}
              onRetry={onRetry}
              canManage={canManage}
              onAddForPartner={onAddForPartner}
              onChange={onChange}
              onRemove={onRemove}
            />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  </>
);

PartnerPreferenceRow.propTypes = {
  partner: PropTypes.shape({
    partnerId: PropTypes.string.isRequired,
    partnerName: PropTypes.string.isRequired,
    preferenceCount: PropTypes.number,
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  preferences: PropTypes.arrayOf(PropTypes.shape({ productId: PropTypes.string })),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  canManage: PropTypes.bool.isRequired,
  onAddForPartner: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onCopyPartnerId: PropTypes.func,
  partnerStatus: PropTypes.oneOf(Object.keys(PARTNER_STATUS_CHIP)),
};

PartnerPreferenceRow.defaultProps = {
  preferences: [],
  loading: false,
  error: '',
  onCopyPartnerId: undefined,
  partnerStatus: undefined,
};

export default PartnerPreferenceRow;
